<script setup lang="ts">
import type { AiCompanionSessionCandidate } from "@simbelapp/order-sdk";
import LoadButton from "~/components/Common/Buttons/LoadButton.vue";
import RadioCard from "~/components/Common/Inputs/RadioCard.vue";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import { useFormats } from "~/composables/utils/useFormat";
import { useAttendanceReviewStore } from "~/store/attendance/attendance-review.store";
import {
  attendanceCandidateDateLabel,
  attendanceSessionChoicePrompt,
  isAttendanceCandidateLocked,
} from "~/utils/helpers/attendance/session-candidates";

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
  }>(),
  {
    disabled: false,
  },
);

const store = useAttendanceReviewStore();
const formats = useFormats();

/** The card picked but not yet sent. Confirming is what answers — a radio no longer does. */
const pendingSessionId = ref<string | null>(null);

/**
 * The card the radio marks: the pick being staged, then the one in flight, then the one the run
 * committed to.
 */
const selectedSessionId = computed({
  get: () => store.choosingSessionId ?? store.chosenSessionId ?? pendingSessionId.value,
  set: (sessionId) => {
    // Once the run has moved on the picker is a record: only a pending choice may still be staged.
    if (!store.hasSessionChoice) return;

    pendingSessionId.value = String(sessionId);
  },
});

/** The question the cards answer: which training is ambiguous, and what to do about it. */
const choicePrompt = computed(() => attendanceSessionChoicePrompt(store.candidates));

/** A choice already on its way is not re-sent: a resumed run takes no second answer. */
const canConfirm = computed(
  () => store.hasSessionChoice && !props.disabled && !store.choosingSessionId && !!pendingSessionId.value,
);

/** The button's own spinner, held by `force-waiting` until the call comes back. */
const isChoosing = ref(false);

/** "Valider le choix de session" — the answer the radio no longer sends by itself. */
async function confirmChoice(): Promise<void> {
  const sessionId = pendingSessionId.value;

  isChoosing.value = true;
  try {
    if (sessionId) await store.chooseSession(sessionId);
  } finally {
    // Reset whatever the store answered, or a refused choice leaves the button spinning for good.
    isChoosing.value = false;
  }
}

/*
 * A correction ("la bonne session est …") runs the detection again on the same paused run and
 * returns a fresh list. That list is a new question, so it takes a new answer: the staged pick goes,
 * even when the session it named is offered again. Left armed, one click would send an answer given
 * before the correction was read. The stream hands each part to the store once, so this only ever
 * fires on a genuine re-detection.
 */
watch(
  () => store.candidates,
  () => {
    pendingSessionId.value = null;
  },
);

function isLocked(candidate: AiCompanionSessionCandidate): boolean {
  return isAttendanceCandidateLocked(candidate, store.choosingSessionId ?? store.chosenSessionId, props.disabled);
}

function dateLabel(candidate: AiCompanionSessionCandidate): string | null {
  return attendanceCandidateDateLabel(candidate, formats.toDateDisplay);
}
</script>

<template>
  <div class="flex flex-col gap-2.5">
    <!-- The mismatch warning first: it is why a session is being asked for at all. -->
    <span v-if="store.warning" class="body_regular text-secondary-color">{{ store.warning }}</span>

    <!-- The question, while it is still one. Answered, the list stays on as the record of the answer. -->
    <span v-if="store.hasSessionChoice" class="body_regular text-primary-color">{{ choicePrompt }}</span>

    <RadioCard
      v-for="candidate in store.candidates"
      :key="candidate.sessionId"
      v-model="selectedSessionId"
      :value="candidate.sessionId"
      :disabled="isLocked(candidate)"
    >
      <div class="flex min-w-0 flex-1 flex-col gap-0.5">
        <div class="flex min-w-0 flex-wrap items-center gap-1.5 text-primary-color">
          <SvgIcon name="graduation_cap_on_v2" class="h-4 w-4 shrink-0" />
          <span class="body_demi">{{ candidate.trainingTitle || "Formation" }}</span>
          <template v-if="candidate.trainingPartner">
            <span class="body_regular text-secondary-color">·</span>
            <span class="body_regular text-secondary-color">{{ candidate.trainingPartner }}</span>
          </template>
        </div>

        <div class="flex min-w-0 flex-wrap items-center gap-x-3.5 gap-y-1 text-secondary-color">
          <div v-if="dateLabel(candidate)" class="flex items-center gap-1.5">
            <SvgIcon name="date" class="h-4 w-4 shrink-0" />
            <span class="meta_regular">{{ dateLabel(candidate) }}</span>
          </div>
          <div v-if="candidate.location" class="flex items-center gap-1.5">
            <SvgIcon name="pin" class="h-4 w-4 shrink-0" />
            <span class="meta_regular">{{ candidate.location }}</span>
          </div>
        </div>
      </div>
    </RadioCard>

    <!--
      Picking stages the choice, this sends it. A radio that resumed the run by itself left a
      mis-click no way back: the attendance is then written against the session it named.
      Enter needs its own binding: the button cancels that keydown's default action, so activating it
      from the keyboard reaches no click handler.
    -->
    <LoadButton
      v-if="store.hasSessionChoice"
      v-model="isChoosing"
      class="w-full"
      text-confirm="Valider le choix de session"
      :disabled="!canConfirm"
      force-waiting
      @click="confirmChoice()"
      @keydown.enter="confirmChoice()"
    />
  </div>
</template>
